import { useEffect, useState } from 'react';
import { useStyle } from '@altermaind/web/ui';

/**
 * This hook calculates the dynamic width for a drawer component based on the size of the body element.
 * It takes into consideration the gutter size derived from the gridGutter style and updates the width on window resize.
 * * The `bodySize` is determined by the width of the first `div` with `role="main"` inside the body of the `PageLayout` component.
 * This size represents the main space that the user sees, excluding the size of the sidebar.
 *
 * @returns {number | string} The calculated dynamic width of the drawer.
 *
 * Dependencies:
 * - useState: A React hook to manage the state of the drawer's dynamic width.
 * - useEffect: A React hook to add a resize event listener that updates the drawer's dynamic width.
 * - useStyle: A custom hook (assumed) that returns the style properties for a given style identifier.
 *
 * Functionality:
 * - Calculates the body width by querying the body element that has a role="main".
 * - Subtracts the double gutter size from the body width to get the drawer's dynamic width.
 * - Returns '60%' if the body size is not available or if the body size is smaller than the gutter.
 * - Updates the drawer's dynamic width whenever the window is resized.
 *
 * Example usage:
 * ```
 * const dynamicWidth = useCalculateDrawerDynamicWidth();
 * console.log(dynamicWidth); // Logs the calculated dynamic width of the drawer.
 * ```
 */
export const useCalculateDrawerDynamicWidth = (): number | string => {
  const minimalPercentage = '60%';
  const [drawerDynamicWidth, setDrawerDynamicWidth] = useState<number | string>(minimalPercentage);
  const parsedGutter = useStyle({
    width: 'gridGutter',
  });
  const gutter = Number(`${parsedGutter?.width ?? ''}`.replace('px', '')) * 2;

  function getBrowserScrollbarWidth() {
    const scrollBarDelta = window.innerWidth - document.documentElement.clientWidth;
    if (scrollBarDelta > 0) {
      return scrollBarDelta;
    }
    return 0;
  }

  const dynamicWidth = () => {
    const scrollbarWidth = getBrowserScrollbarWidth();
    const bodySize = document.querySelector('body div[role="main"]')?.clientWidth;
    if (bodySize) {
      if (bodySize > gutter) {
        return bodySize - gutter + scrollbarWidth;
      }
      return bodySize;
    }
    return minimalPercentage;
  };

  useEffect(() => {
    setDrawerDynamicWidth(dynamicWidth());

    const handleResize = () => {
      setDrawerDynamicWidth(dynamicWidth());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return drawerDynamicWidth;
};
